import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import pepperLogo from '../../assets/images/Pepper_logo.svg';
import { ROUTES_PATH } from '../../constants';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import {
  CreditProposalFlowStep,
  CreditProposalResult,
} from '../../interfaces/CreditProposalResult';
import { DecisionStatus } from '../../interfaces/DecisionEngine';
import CreditProposalService from '../../services/CreditProposal';
import LoanSimulationService from '../../services/LoanSimulation';
import TokenService from '../../services/Token';
import { LogoBlink, ContainerLogo } from './LandingStyledComponent';
import { useLoanSimulation } from './../../contexts/LoanSimulationContext/loan-context.hook';
import ViewModeService from './../../services/ViewMode/index';
import { useCustomer } from '../../contexts/CustomerContext';
import InsightService from './../../services/Insight/index';
import { useCustomerClassification } from '../../contexts/CustomerClassificationContext';
import { CommercialOfferType } from '../../services/LoanSimulation/types';
import { LoanSimulation, stateType } from '../../interfaces/LoanSimulation';
import {
  CustomerClassification,
  CustomerClassificationEnum,
} from '../../interfaces/CustomerClassification';
import SecurityService from '../../services/Security';
import CustomerDeviceService from '../../services/CustomerDevice';
import { useWebsocket } from './../../contexts/WebsocketContext/websocket-context.hook';
import MerchantService from '../../services/Merchant';
import { CommercialOffer } from '../../interfaces/CommercialOffer';
import {
  fetchCommercialOffersList,
  getCommercialOffersStored,
  getTimeDifference,
  setCommercialOffersStored,
} from '../../utils/commercialOffersHelper';
import { Transition } from '../Transition';
import { useProduct } from '../../contexts/ProductContext';
import { GoodTypes } from '../../interfaces/MerchantInfo';
import { useMerchant } from '../../contexts/MerchantContext';
import { useFingerprint } from '../../contexts/FingerprintContext';
import fplibrary from '@pepper/fp-pepper';
import AnalyticsService from '../../services/Analytics';
import { DenialStep } from '../../interfaces/Analytics';
import FlowEndService from '../../services/FlowEndService';

export const Landing = () => {
  const { deleteCustomer, createCustomer } = useCustomer();
  const search = useLocation().search;
  const uuid = new URLSearchParams(search).get('credit_proposal_id');
  const token = new URLSearchParams(search).get('lead_token');
  const authToken = new URLSearchParams(search).get('authetication_token');
  const retaken = new URLSearchParams(search).get('retaken');
  // OLD APP VERSION
  const viewMode = new URLSearchParams(search).get('viewMode');
  const device = new URLSearchParams(search).get('device');
  //  *************
  const amount = new URLSearchParams(search).get('amount');
  const term = new URLSearchParams(search).get('term');
  // FROM CUSTOMER DEVICE
  const customerDevice = new URLSearchParams(search).get('customer_device');
  const loanId = new URLSearchParams(search).get('loan_id');
  const proposalId = new URLSearchParams(search).get('refinanced_proposal');
  // SHARED COMERCIAL OFFERS FROM PEPPER COMERCIOS
  const sharedOffers = new URLSearchParams(search).get('shared_offers');
  // SUPER EXPRESS MODE V1
  const superExpress = new URLSearchParams(search).get('super_express');
  const commercialOfferId = new URLSearchParams(search).get(
    'commercial_offer_id',
  );

  // DORSIA PARAMS
  const product = new URLSearchParams(search).get('product');
  const customerName = new URLSearchParams(search).get('name');
  const customerSurname = new URLSearchParams(search).get('surname');
  const customerEmail = new URLSearchParams(search).get('email');
  const customerPhone = new URLSearchParams(search).get('phone');
  const customerDocumentId = new URLSearchParams(search).get('document_id');
  const customerBirthDate = new URLSearchParams(search).get('birth_date');
  const inputMode = new URLSearchParams(search).get('input_mode');

  // IDENTIFIER FOR POST MESSAGES (NEMURU, DORSIA, ETC)
  const postDataMessages = new URLSearchParams(search).get(
    'post_data_messages',
  );
  // *************
  // APP IDENTIFIER FOR SIGNATURE CONTRACT LINK
  const isApp = new URLSearchParams(search).get('is_app') === 'true';
  // *************

  let hasData = false;

  const navigate = useNavigate();

  const { createCreditProposalResult, deleteCreditProposalResult } =
    useCreditProposalResult();
  const { createProduct } = useProduct();
  const { createLoanSimulation } = useLoanSimulation();
  const { createCustomerClassification } = useCustomerClassification();
  const { deleteWebsocketTexts } = useWebsocket();
  const { createNemuru, createIsApp, createDataMessages } = useMerchant();
  const { fingerprint, createFingerprint } = useFingerprint();

  const getDecisionData = async (
    creditProposal: CreditProposalResult,
    counter: number,
    step?: string,
  ) => {
    counter++;
    try {
      const decisionResp = await CreditProposalService.getDecision(
        creditProposal.credit_proposal_id,
      );
      if (decisionResp && step === 'DECISION_PAC') {
        // Pago ya realizado: decisión PAC, igual que Payment.getPADecision.
        // Debe evaluarse antes que customerDevice para no volver a OCR/PAYMENT.
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
          case DecisionStatus.NOT_NECESSARY:
            navigate(ROUTES_PATH.SIGNATURE, {
              state: {
                decisionStatus: { signatureReady: true, isDecisionPending: false },
              },
            });
            break;
          case DecisionStatus.PENDING:
            navigate(ROUTES_PATH.SIGNATURE, {
              state: {
                decisionStatus: { signatureReady: true, isDecisionPending: true },
              },
            });
            break;
          case DecisionStatus.DENIED:
            AnalyticsService.sendLoanRefused({ denial_step: DenialStep.PAC });
            navigate(ROUTES_PATH.DENIAL);
            break;
          default:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
        }
      } else if (decisionResp) {
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
            if (customerDevice?.toLocaleLowerCase() === 'true') {
              if (
                creditProposal.customer?.require_documentation &&
                !creditProposal.ocr_id &&
                creditProposal.product !== 'PLS'
              ) {
                navigate(ROUTES_PATH.OCR);
              } else if (creditProposal.product === 'PLS') {
                navigate(ROUTES_PATH.BANKREADER);
              } else {
                navigate(ROUTES_PATH.PAYMENT);
              }
            } else {
              navigate(ROUTES_PATH.OPERATION_SHIPPING);
            }
            break;
          case DecisionStatus.ALTERNATIVE_OFFER:
          case DecisionStatus.COUNTER_OFFER: // Contraoferta
            if (
              decisionResp.counter_offers &&
              decisionResp.counter_offers.length > 0
            ) {
              createCreditProposalResult({
                ...creditProposal,
                counter_offers: decisionResp.counter_offers,
              } as CreditProposalResult);
            }
            navigate(ROUTES_PATH.COUNTER_OFFER);
            break;
          case DecisionStatus.DENIED:
            navigate(ROUTES_PATH.DENIAL);
            break;
          case DecisionStatus.MISSING_DATA:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
          default:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
        }
      }
    } catch (error) {
      if (`${error}`.includes('409') && counter <= 11) {
        setTimeout(() => {
          getDecisionData(creditProposal, counter, step);
        }, 3000);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const getClassification = async (creditProposal: CreditProposalResult) => {
    try {
      const classification = await CreditProposalService.postClassification(
        creditProposal.credit_proposal_id,
        creditProposal,
      );
      createCustomerClassification(classification);
      if (
        classification.customer_classification ===
        CustomerClassificationEnum.denied
      ) {
        AnalyticsService.sendLoanRefused({ denial_step: DenialStep.CUST });
        navigate(ROUTES_PATH.DENIAL);
      } else if (
        classification.require_labor_data ||
        classification.require_address_data
      ) {
        navigate(ROUTES_PATH.LABOR_DATA);
      } else {
        getDecisionData(creditProposal, 0);
      }
    } catch (error: any) {
      if (
        error &&
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0089',
        )
      ) {
        navigate(ROUTES_PATH.PERSONAL_FINANCES_UNAVAILABLE, {
          state: { errorType: 'DRF0089' },
        });
      } else if (
        error &&
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0092',
        )
      ) {
        //2º caso de error de Belender (jubilados)
        navigate(ROUTES_PATH.PERSONAL_FINANCES_UNAVAILABLE, {
          state: { errorType: 'DRF0092' },
        });
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const handleMissingData = async (
    creditProposal: CreditProposalResult,
    step?: string,
  ) => {
    if (step === 'DECISION_PAC') {
      getDecisionData(creditProposal, 0, 'DECISION_PAC');
    } else {
      if (creditProposal.customer?.customer_classification) {
        if (
          creditProposal.customer.require_labor_data ||
          creditProposal.customer.require_address_data
        ) {
          navigate(ROUTES_PATH.LABOR_DATA);
        } else {
          getDecisionData(creditProposal, 0);
        }
      } else {
        getClassification(creditProposal);
      }
    }
  };

  const verifyApiCall = async (
    counter: number,
    creditProposalId: string,
    isVerify: boolean,
    isNemuru: boolean,
  ) => {
    const count = counter;
    try {
      if (isVerify) {
        !isNemuru &&
          (await CreditProposalService.verifyBankreader(creditProposalId));
        AnalyticsService.verify();
        navigate(ROUTES_PATH.END);
      } else {
        !isNemuru &&
          (await CreditProposalService.activateCreditProposal(
            creditProposalId,
          ));
        navigate(ROUTES_PATH.END);
      }
    } catch (error: any) {
      const errorCode = error.response?.data.result.errors[0]?.code;
      if (
        error.result?.http_code === 422 &&
        errorCode === 'DRF0027' &&
        count < 3
      ) {
        setTimeout(() => {
          verifyApiCall(count + 1, creditProposalId, isVerify, isNemuru);
        }, 3000);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const getCreditProposal = async (
    creditProposalId: string,
    offersSharedData?: CommercialOffer[],
  ) => {
    try {
      const creditProposalResult =
        await CreditProposalService.getCreditProposal(creditProposalId);
      if (creditProposalResult?.product === 'SUS') {
        sessionStorage.setItem('Flow-SUS', 'true');
      }
      // Se calcula aquí porque el contexto de Nemuru no está actualizado
      // hasta el siguiente render y verifyApiCall lo necesita ya
      const isNemuruProposal =
        creditProposalResult.product === 'ECO' &&
        creditProposalResult.application === 'ANM';
      if (isNemuruProposal) {
        createNemuru(true); // Set Nemuru context
        createDataMessages(true); // Set DataMessages context
      }
      createCreditProposalResult(creditProposalResult);
      creditProposalResult.customer &&
        createCustomer(creditProposalResult.customer);

      if (creditProposalResult.customer?.customer_classification) {
        const classification: CustomerClassification = {
          customer_classification:
            creditProposalResult.customer.customer_classification,
          require_documentation:
            creditProposalResult.customer.require_documentation &&
            !creditProposalResult.ocr_id,
          require_labor_data: creditProposalResult.customer.require_labor_data!,
          require_secure_payment:
            creditProposalResult.customer.require_secure_payment!,
          require_address_data:
            creditProposalResult.customer.require_address_data!,
          require_enrichment_personal_finances:
            creditProposalResult.customer.require_enrichment_personal_finances!,
        };
        createCustomerClassification(classification);
      }
      if (authToken) {
        getLoanSimulation(creditProposalResult);
        navigate(ROUTES_PATH.CONTINUATION);
      } else if (token) {
        if (retaken) {
          InsightService.logTrace('Retaken', [
            {
              retaken: retaken,
            },
          ]);
          await getCommerOffersList(offersSharedData);
        } else if (creditProposalResult.status === 'ACT') {
          navigate(ROUTES_PATH.END);
        } else if (creditProposalResult.status === 'WTD') {
          navigate(ROUTES_PATH.DENIAL);
        } else {
          switch (creditProposalResult.flow_step) {
            case CreditProposalFlowStep.PAYMENT:
              navigate(ROUTES_PATH.PAYMENT);
              break;
            case CreditProposalFlowStep.OCR:
              if (customerDevice?.toLocaleLowerCase() === 'true') {
                navigate(ROUTES_PATH.OCR);
              } else {
                navigate(ROUTES_PATH.OPERATION_SHIPPING);
              }
              break;
            case CreditProposalFlowStep.ESIGNATURE:
              if (customerDevice?.toLocaleLowerCase() === 'true') {
                navigate(ROUTES_PATH.SIGNATURE);
              } else {
                navigate(ROUTES_PATH.OPERATION_SHIPPING);
              }
              break;
            case CreditProposalFlowStep.DECISION:
              handleMissingData(creditProposalResult);
              break;
            case CreditProposalFlowStep.DECISION_PAC:
              handleMissingData(creditProposalResult, 'DECISION_PAC');
              break;
            case CreditProposalFlowStep.ACTIVATION:
              if (customerDevice?.toLocaleLowerCase() === 'true') {
                verifyApiCall(0, creditProposalId, false, isNemuruProposal);
              } else {
                navigate(ROUTES_PATH.OPERATION_SHIPPING);
              }
              break;
            case CreditProposalFlowStep.VERIFICATION:
              if (customerDevice?.toLocaleLowerCase() === 'true') {
                verifyApiCall(0, creditProposalId, true, isNemuruProposal);
              } else {
                navigate(ROUTES_PATH.OPERATION_SHIPPING);
              }
              break;
            case CreditProposalFlowStep.CLASSIFICATION:
              getClassification(creditProposalResult);
              break;
            case CreditProposalFlowStep.UPDATE:
              navigate(ROUTES_PATH.LABOR_DATA);
              break;
            case CreditProposalFlowStep.UPDATE_IBAN:
            case CreditProposalFlowStep.BANK_READER:
              // BANK_READER
              if (customerDevice?.toLocaleLowerCase() === 'true') {
                navigate(ROUTES_PATH.BANKREADER);
              } else {
                navigate(ROUTES_PATH.OPERATION_SHIPPING);
              }
              break;
            case CreditProposalFlowStep.COUNTER_OFFER: // Contraoferta
              if (
                creditProposalResult.counter_offers &&
                creditProposalResult.counter_offers.length > 0
              ) {
                createCreditProposalResult({
                  ...creditProposalResult,
                  counter_offers: creditProposalResult.counter_offers,
                  flow_step: CreditProposalFlowStep.COUNTER_OFFER,
                } as CreditProposalResult);
              }
              navigate(ROUTES_PATH.COUNTER_OFFER);
              break;
            case CreditProposalFlowStep.PERSONAL_FINANCES:
              navigate(ROUTES_PATH.PERSONAL_FINANCES);
              break;
            default:
              await getCommerOffersList(offersSharedData);
              break;
          }
        }
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } catch (error) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getLoanSimulation = async (
    creditProposal: CreditProposalResult | null,
    expressData?: any,
  ) => {
    const simulationBody = creditProposal ?? expressData;
    const sim = {
      amount: simulationBody.requested_amount!,
      flows: ['CEX', 'PLS', 'SUS'],
      term: simulationBody.term,
    };
    const commercialOffers = await LoanSimulationService.create(sim);
    const offerSelected = commercialOffers.commercial_offers.find(
      (e: CommercialOfferType) =>
        e.commercial_offer_id === simulationBody.commercial_offer_id,
    );
    const simulations = offerSelected?.financial_offers;
    if (simulations) {
      const response = simulations.find((e: LoanSimulation) => {
        return e.term === simulationBody.term;
      });
      if (response && simulationBody.commercial_offer_id) {
        response.commercial_offer_id = simulationBody.commercial_offer_id;
        response.flow = offerSelected.flow;
        createLoanSimulation(response);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } else {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getMerchantData = async (): Promise<string> => {
    try {
      const merchantId = sessionStorage.getItem('merchantId');
      const merchantGoodTypes = sessionStorage.getItem('merchantGoodTypes');
      if (!merchantId || !merchantGoodTypes) {
        const response = await MerchantService.getMerchantInfo();
        sessionStorage.setItem('merchantId', response.merchant_id);
        sessionStorage.setItem(
          'merchantGoodTypes',
          JSON.stringify(response.good_types),
        );
        if (response.good_types && amount && product) {
          const productType = response.good_types?.find(
            (pr: GoodTypes) => pr.code === product,
          );

          createProduct(productType, parseFloat(amount));
        }
        return response.merchant_id;
      } else if (merchantId) {
        return merchantId;
      }
    } catch (error) {
      return '';
    }
    return '';
  };

  const getCommerOffersList = async (offersSharedData?: CommercialOffer[]) => {
    try {
      let merchantInfo = await getMerchantData();
      if (offersSharedData) {
        setCommercialOffersStored(offersSharedData, merchantInfo);
      } else {
        const offersL = getCommercialOffersStored();
        const isValidDate = offersL
          ? getTimeDifference(offersL.timestamp)
          : false;
        if (
          !isValidDate ||
          offersL?.merchant_id !== merchantInfo ||
          !merchantInfo
        ) {
          const totalOffers = await fetchCommercialOffersList();
          setCommercialOffersStored(totalOffers, merchantInfo);
        }
      }
      !superExpress &&
        navigate(ROUTES_PATH.ENTRY, {
          state: amount,
        });
    } catch (error) {
      console.error(error);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const authenticateToken = async (
    tokenS: string,
    offersSharedData?: CommercialOffer[],
  ) => {
    try {
      let encodedToken = encodeURIComponent(tokenS);
      encodedToken = encodedToken?.replace(new RegExp('%20', 'g'), '%2B');
      const decodedToken = decodeURIComponent(encodedToken);
      const tokenResponse = await SecurityService.authenticate(decodedToken);
      TokenService.setToken(tokenResponse.access_token);
      if (uuid) {
        getCreditProposal(uuid, offersSharedData);
      } else if (superExpress) {
        if (amount && term && commercialOfferId && product) {
          sessionStorage.setItem('CEX-GoodType', product);
          const simulationData = {
            requested_amount: parseFloat(amount),
            term: parseInt(term),
            commercial_offer_id: commercialOfferId,
          };
          await getCommerOffersList(offersSharedData);
          await getLoanSimulation(null, simulationData);
          navigate(ROUTES_PATH.PERSONAL_DATA);
        } else {
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
        }
      } else if (loanId && proposalId) {
        const stateParams: stateType = {
          loanId: loanId,
          refinancedProposal: proposalId,
        };
        sessionStorage.setItem('Flow-SUS', 'true');
        navigate(ROUTES_PATH.ENTRY_SUS, {
          state: stateParams,
        });
      } else {
        await getCommerOffersList(offersSharedData);
      }
    } catch {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const offersListener = (event: MessageEvent) => {
    if (event.origin === import.meta.env.VITE_COMERCIOS_URL) {
      hasData = true;
      if (token) {
        deleteWebsocketTexts();
        authenticateToken(token, event.data.simulationCommercialOffers);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
      window.removeEventListener('message', offersListener);
    }
  };

  const setInitV = () => {
    //Limpieza de parámetros previos
    sessionStorage.removeItem('merchatnId');
    sessionStorage.removeItem('merchantGoodTypes');
    sessionStorage.removeItem('Flow-SUS');
    sessionStorage.removeItem('amount');
    sessionStorage.removeItem('customerData');
    sessionStorage.removeItem('inputMode');
    sessionStorage.removeItem('CEX-GoodType');
    sessionStorage.removeItem('p_signature');

    deleteCreditProposalResult();
    deleteCustomer();

    if (customerDevice) {
      CustomerDeviceService.setCustomerDevice(
        customerDevice.toLocaleLowerCase(),
      );
    }

    if (viewMode) {
      ViewModeService.setViewMode(viewMode);
    }

    if (device) {
      ViewModeService.setDevice(device);
    }

    if (amount) {
      sessionStorage.setItem('amount', JSON.stringify(amount));
    }
    if (
      customerName ||
      customerSurname ||
      customerEmail ||
      customerPhone ||
      customerDocumentId ||
      customerBirthDate
    ) {
      let customerData = {};
      if (customerName) {
        Object.assign(customerData, { name: customerName });
      }
      if (customerSurname) {
        Object.assign(customerData, { surname: customerSurname });
      }
      if (customerEmail) {
        Object.assign(customerData, { email: customerEmail });
      }
      if (customerPhone) {
        Object.assign(customerData, { phone: customerPhone });
      }
      if (customerDocumentId) {
        Object.assign(customerData, { document_id: customerDocumentId });
      }
      if (customerBirthDate) {
        Object.assign(customerData, { birth_date: customerBirthDate });
      }
      sessionStorage.setItem('customerData', JSON.stringify(customerData));
    }

    if (inputMode) {
      sessionStorage.setItem('inputMode', JSON.stringify(inputMode));
    }
  };

  const handleBackUp = () => {
    window.removeEventListener('message', offersListener);
    if (!hasData) {
      if (token) {
        deleteWebsocketTexts();
        authenticateToken(token);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const initFingerprint = async () => {
    const fp = await fplibrary.load();
    const result = await fp.get();
    createFingerprint(result);
  };

  useEffect(() => {
    FlowEndService.clearPepperOneEnded();
    if (!fingerprint) {
      initFingerprint();
    }
    if (sharedOffers === 'true') {
      window.addEventListener('message', offersListener, false);
      setInitV();
      setTimeout(() => {
        handleBackUp();
      }, 3000);
    } else {
      setInitV();
      if (token) {
        deleteWebsocketTexts();
        authenticateToken(token);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
    if (isApp) {
      createIsApp(true);
    }
    if (postDataMessages) {
      // Se activa el modo envío de datos por post messages (Nemuru, Dorsia, etc)
      createDataMessages(true);
    }
    return () => {
      window.removeEventListener('message', offersListener);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return !!superExpress ? (
    <Transition />
  ) : (
    <ContainerLogo>
      <LogoBlink src={pepperLogo} alt="" />
    </ContainerLogo>
  );
};
